/**
 * Reine Helfer fuer den Bundle-Download unter ANHAENGE auf der Talent-Seite.
 * Kein React, kein Netzwerk, damit die Logik ohne Rendering testbar bleibt.
 *
 * Bewusst ohne Import aus `./index`, das zieht `react-image-file-resizer` in
 * jeden Jest-Lauf, der diese Datei anfasst.
 */

import { MATCHINGPROFILE, TALENTPROFILE } from '../constants/evaluation-types'

/**
 * Deutsch und English, mehr gibt es nicht. Das Stärkenprofil fuehrt Nederlands
 * als "coming soon", das Passungsprofil kennt es gar nicht, ein Bundle mit einer
 * halb uebersetzten Datei waere schlechter als keine Option.
 */
export const BUNDLE_LANGUAGES = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' }
]

const BUNDLE_LANGUAGE_VALUES = BUNDLE_LANGUAGES.map(({ value }) => value)

// Das Backend prueft die ersten Bytes auf %PDF- und laesst alles andere weg. Die
// Endung entscheidet nur, was der Dialog ueberhaupt zur Auswahl anbietet.
const BUNDLE_DOCUMENT_EXTENSIONS = ['pdf']

// Die Profile, die am Talent haengen. Standardreihenfolge wie die Kacheln.
const BUNDLE_PROFILE_TYPES = [MATCHINGPROFILE, TALENTPROFILE]

// Windows verbietet diese Zeichen im Dateinamen, macOS und Linux stolpern ueber
// den Schraegstrich. Umlaute bleiben, die vertraegt heute jedes Dateisystem.
const FORBIDDEN_IN_FILENAME = /[\\/:*?"<>|]/g

// `error` kommt aus dem Backend und geht ueber console.error nach Bugsnag. Heute
// sind das feste kurze Saetze, aber gebremst wird es trotzdem. Eine URL in einer
// Fehlermeldung waere ein signierter Link, und Bugsnag ist kein Ort dafuer.
const MAX_REASON_LENGTH = 200
const URL_IN_TEXT = /\bhttps?:\/\/\S+/gi

const toSafeReason = (value) => {
  const text = String(value).replace(URL_IN_TEXT, '[url]').trim()

  return text.length > MAX_REASON_LENGTH
    ? `${text.slice(0, MAX_REASON_LENGTH)}...`
    : text
}

/**
 * Die Aivy-Profile, die es fuer das Talent gibt, in der Reihenfolge der Kacheln
 * unter ANHAENGE. Sie ist zugleich die Seitenreihenfolge im fertigen PDF.
 *
 * Das Passungsprofil braucht ein abgeschlossenes Assessment und eine Stelle, das
 * Staerkenprofil gibt es ab dem ersten Spiel. REQUIREMENTPROFILE und TEAMPROFILE
 * kann das Backend auch buendeln, sie haengen aber nicht am Talent.
 *
 * @param {Object} input
 * @param {number} [input.progress]
 * @param {string} [input.careerId]
 * @returns {Array<string>}
 */
export const getAvailableProfileTypes = ({ progress, careerId } = {}) =>
  [
    progress === 100 && !!careerId && MATCHINGPROFILE,
    progress > 0 && TALENTPROFILE
  ].filter(Boolean)

/**
 * Die Profile in der Reihenfolge, die der Kunde zuletzt gezogen hat. Was die
 * gespeicherte Reihenfolge nicht kennt, bleibt in seiner Standardposition
 * dahinter, damit ein Profil nie aus der Liste faellt.
 *
 * @param {Array<string>} profileTypes aus getAvailableProfileTypes
 * @param {Array<string>} [preferredOrder]
 * @returns {Array<string>}
 */
export const sortProfileTypes = (profileTypes, preferredOrder) => {
  if (!Array.isArray(profileTypes)) return []
  if (!Array.isArray(preferredOrder)) return [...profileTypes]

  const known = preferredOrder.filter((type) => profileTypes.includes(type))
  const rest = profileTypes.filter((type) => !known.includes(type))

  return [...new Set([...known, ...rest])]
}

/**
 * Eine Position nach oben (-1) oder unten (1). Am Rand bleibt die Liste gleich,
 * dieselbe Referenz kommt zurueck, damit der Aufrufer "nichts passiert" erkennt.
 *
 * @param {Array} list
 * @param {number} index
 * @param {number} delta
 * @returns {Array}
 */
export const moveItem = (list, index, delta) => {
  const target = index + delta
  if (index < 0 || index >= list.length) return list
  if (target < 0 || target >= list.length) return list

  const next = [...list]
  const [item] = next.splice(index, 1)
  next.splice(target, 0, item)

  return next
}

/**
 * Gespeicherte Reihenfolge aus dem localStorage. Alles, was kein bekanntes
 * Profil ist, fliegt raus, ein kaputter Wert ergibt die Standardreihenfolge.
 *
 * @param {*} raw
 * @returns {Array<string>|null}
 */
export const parseStoredProfileOrder = (raw) => {
  if (typeof raw !== 'string' || !raw) return null

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null

    const order = [
      ...new Set(parsed.filter((type) => BUNDLE_PROFILE_TYPES.includes(type)))
    ]

    return order.length > 0 ? order : null
  } catch (parseError) {
    return null
  }
}

/**
 * Die Dokumente, die der Dialog anbietet, aus `listDocuments`. Ersetzte oder
 * gelöschte Dateien liefert listFilesForSpace gar nicht mehr.
 *
 * `name` ist der Dateiname ohne Pfad, genau so erwartet ihn `document_names`.
 *
 * @param {Array<{ meta: Object }>} entries
 * @returns {Array<{ name: string, displayName: string, extension: string, uploadedAt: Date|null, questionLabel: string, bundleable: boolean }>}
 */
export const getBundleDocuments = (entries) => {
  if (!Array.isArray(entries)) return []

  return entries
    .map((entry) => entry && entry.meta)
    .filter((meta) => meta && meta.filename)
    .map((meta) => ({
      name: meta.filename,
      displayName: meta.displayName || meta.filename,
      extension: meta.extension || '',
      uploadedAt: meta.uploadedAt || null,
      questionLabel: meta.questionLabel || '',
      bundleable: BUNDLE_DOCUMENT_EXTENSIONS.includes(meta.extension)
    }))
}

/**
 * Lohnt sich der Dialog? Das Backend braucht mindestens ein Profil, Dokumente
 * allein beantwortet es mit "missing paramter". Und ein einzelnes Profil ohne
 * Dokument ist dieselbe Datei wie der Download an der Kachel.
 *
 * @param {Object} input
 * @param {Array<string>} input.profileTypes
 * @param {Array<{ bundleable: boolean }>} input.documents
 * @returns {boolean}
 */
export const canExportBundle = ({ profileTypes = [], documents = [] } = {}) =>
  profileTypes.length > 0 &&
  profileTypes.length + documents.filter((doc) => doc.bundleable).length > 1

/**
 * Variablen fuer `generatePdfBundle`.
 *
 * `base64` ist immer true, ohne das Flag antwortet das Backend auf ein Bundle mit
 * 400. `show_old_ui` taucht nie auf, es waere im Bundle ohnehin wirkungslos,
 * sobald ein Career geladen ist. `document_names` faellt ganz weg, wenn keine
 * Dokumente gewaehlt sind, dann laedt das Backend auch den Space nicht.
 *
 * @param {Object} input
 * @returns {Object}
 */
export const buildBundleVariables = ({
  spaceId,
  userId,
  careerId,
  language,
  types,
  documentNames
}) => {
  const document_names = Array.isArray(documentNames)
    ? [...new Set(documentNames.filter((name) => typeof name === 'string'))]
    : []

  return {
    user_id: userId,
    career_id: careerId,
    space_id: spaceId,
    language: BUNDLE_LANGUAGE_VALUES.includes(language) ? language : 'de',
    types,
    ...(document_names.length > 0 && { document_names }),
    base64: true
  }
}

/**
 * Antwort von `generatePdf` auswerten.
 *
 * Geprueft wird ausschliesslich auf `pdf_url`. Das Lambda endet im Fehlerfall mit
 * `return error` auf einer Error-Instanz, und die serialisiert zu `{}`, weil
 * `message` nicht enumerable ist. Ein Client, der auf `statusCode` oder `error`
 * schaut, steht bei genau diesem Fall ohne Information da.
 *
 * `reason` beschreibt den Fehlschlag so knapp, dass er als Error-Message nach
 * Bugsnag taugt. Ohne den Text sieht jeder Fehler gleich aus und die Suche
 * beginnt bei null, obwohl die Antwort die Ursache schon mitliefert.
 *
 * `skippedDocuments` sind die Dateinamen, die das Backend nicht angehaengt hat,
 * weil sie kein lesbares PDF waren oder fehlten. Das PDF kommt trotzdem, der
 * Kunde soll aber erfahren, dass etwas fehlt.
 *
 * @param {*} raw AWSJSON-String, Objekt, null oder Unsinn
 * @returns {{ ok: boolean, pdfUrl: string|null, contentLength: number|null, skippedDocuments: Array<string>, reason: string|null }}
 */
export const parseGeneratePdfResponse = (raw) => {
  const fail = (reason) => ({
    ok: false,
    pdfUrl: null,
    contentLength: null,
    skippedDocuments: [],
    reason
  })

  if (raw === null || raw === undefined || raw === '') {
    return fail('leere Antwort')
  }

  let payload = raw

  if (typeof raw === 'string') {
    try {
      payload = JSON.parse(raw)
    } catch (parseError) {
      return fail('Antwort ist kein JSON')
    }
  }

  if (!payload || typeof payload !== 'object') {
    return fail('Antwort ist kein Objekt')
  }

  if (typeof payload.pdf_url === 'string' && payload.pdf_url) {
    return {
      ok: true,
      pdfUrl: payload.pdf_url,
      contentLength:
        typeof payload.content_length === 'number'
          ? payload.content_length
          : null,
      skippedDocuments: Array.isArray(payload.parts)
        ? payload.parts
            .filter((part) => part && part.kind === 'document' && part.skipped)
            .map((part) => part.name)
        : [],
      reason: null
    }
  }

  // Genau die Signatur des Lambda-catch. Wenn das kommt, ist die Function
  // abgestuerzt und die Ursache steht nur in CloudWatch.
  if (Object.keys(payload).length === 0) {
    return fail(
      'leeres Objekt, das Lambda ist abgestuerzt (CloudWatch pruefen)'
    )
  }

  // Der Redirect-Pfad. Heisst, base64 ist nicht als true angekommen.
  if (payload.statusCode === 302 || payload.headers) {
    return fail('Redirect statt Bundle, base64 kam nicht als true an')
  }

  if (payload.error) {
    return fail(
      `${payload.statusCode || 'ohne Status'}: ${toSafeReason(payload.error)}`
    )
  }

  return fail(`${payload.statusCode || 'ohne Status'} ohne pdf_url`)
}

/**
 * Ohne eigenen Namen liefert S3 fuer jedes Talent `aivy-report.pdf`, im
 * Download-Ordner liegen dann zehn gleich heissende Dateien.
 *
 * @param {Object} input
 * @param {string} [input.name]     Angezeigter Name des Talents
 * @param {string} [input.fallback] Ersatz, wenn kein Name da ist
 * @returns {string}
 */
export const buildBundleFilename = ({ name, fallback } = {}) => {
  const slug = String(name || fallback || '')
    .toLowerCase()
    .replace(FORBIDDEN_IN_FILENAME, '')
    .replace(/[\s_.]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  return slug ? `${slug}-aivy-profile.pdf` : 'aivy-profile.pdf'
}

/**
 * Datei im Browser anbieten. Das revokeObjectURL steht im setTimeout, weil
 * sofortiges Revoken den Download in Safari abbricht.
 */
export const downloadBlob = ({ blob, filename }) => {
  const objUrl = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.setAttribute('href', objUrl)
  link.setAttribute('download', filename)
  link.click()

  setTimeout(() => URL.revokeObjectURL(objUrl), 0)
}
